import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import { peecAPI } from '../api/peecAPI';
import { PageHeader, PageShell, SectionCard, TableFrame, Td, Th } from '../components/PeecUI';

interface GapAnalysisPageProps {
  workspaceId: string;
}

type GapModel = { id: string; name: string; website: string };
type GapBrand = { id: string; name: string; domain: string; isOwn?: boolean; scores: Record<string, number> };
type SortDirection = 'asc' | 'desc';
type SortField = string;

interface SortState {
  field: SortField;
  direction: SortDirection;
}

function getHeatmapStyle(value: number) {
  const opacity = 0.14 + value / 100 * 0.72;
  return {
    backgroundColor: `rgba(37, 99, 235, ${opacity})`,
    color: value >= 58 ? '#ffffff' : '#0f172a',
  };
}

function getFaviconUrl(url: string) {
  return `https://www.google.com/s2/favicons?sz=64&domain_url=${encodeURIComponent(url)}`;
}

function findModelById(models: GapModel[], modelId: string) {
  return models.find((model) => model.id === modelId) ?? models[0];
}

function compareText(left: string, right: string, direction: SortDirection) {
  const result = left.localeCompare(right, 'en', { sensitivity: 'base' });
  return direction === 'asc' ? result : -result;
}

function compareNumber(left: number, right: number, direction: SortDirection) {
  return direction === 'asc' ? left - right : right - left;
}

const MetricStat: React.FC<{
  label: string;
  value: React.ReactNode;
  delta: string;
}> = ({ label, value, delta }) => (
  <div className="p-5">
    <p className="text-xs text-zinc-400">{label}</p>
    <div className="mt-2 flex items-end gap-2">
      <p className="text-2xl font-semibold tracking-tight text-zinc-950 tabular-nums">{value}</p>
      <span className="pb-1 text-sm tabular-nums text-zinc-400">{delta}</span>
    </div>
  </div>
);

const ModelStat: React.FC<{
  label: string;
  model: GapModel;
}> = ({ label, model }) => (
  <div className="p-5">
    <p className="text-xs text-zinc-400">{label}</p>
    <div className="mt-2 flex items-center gap-3">
      <img src={getFaviconUrl(model.website)} alt="" className="h-8 w-8 object-contain" />
      <div>
        <p className="text-lg font-semibold tracking-tight text-zinc-950">{model.name}</p>
      </div>
    </div>
  </div>
);

const GapAnalysisPage: React.FC<GapAnalysisPageProps> = ({ workspaceId }) => {
  const { t } = useI18n();
  const [data, setData] = useState<any | null>(null);
  const [sort, setSort] = useState<SortState>({ field: 'chatgpt', direction: 'desc' });

  useEffect(() => {
    peecAPI.getGapAnalysis(workspaceId).then(setData).catch((error) => console.error('Failed to fetch gap analysis:', error));
  }, [workspaceId]);

  if (!data) {
    return (
      <PageShell>
        <PageHeader title={t('pages.gapAnalysisTitle')} />
      </PageShell>
    );
  }

  const ownBrand = data.brands.find((brand: GapBrand) => brand.isOwn) ?? data.brands[0];
  const ownEntries = Object.entries(ownBrand.scores) as [string, number][];
  const strongestModel = findModelById(
    data.models,
    ownEntries.reduce((best, current) => (current[1] > best[1] ? current : best))[0]
  );
  const weakestModel = findModelById(
    data.models,
    ownEntries.reduce((worst, current) => (current[1] < worst[1] ? current : worst))[0]
  );

  const sortedBrands = [...data.brands].sort((left: GapBrand, right: GapBrand) => {
    if (sort.field === 'brand') {
      return compareText(left.name, right.name, sort.direction);
    }

    const scoreField = sort.field as keyof typeof left.scores;
    const scoreDelta = compareNumber(left.scores[scoreField], right.scores[scoreField], sort.direction);
    if (scoreDelta !== 0) {
      return scoreDelta;
    }

    return compareText(left.name, right.name, 'asc');
  });

  const headline = `您的品牌 AI 可见度排名第 #${data.summary.rank} — ${data.summary.leaders.join('、')} 等 ${data.summary.remainingLeaders} 个品牌领先`;

  const toggleSort = (field: SortField) => {
    setSort((current) => {
      if (current.field === field) {
        return {
          field,
          direction: current.direction === 'desc' ? 'asc' : 'desc',
        };
      }

      return {
        field,
        direction: field === 'brand' ? 'asc' : 'desc',
      };
    });
  };

  const renderSortIndicator = (field: SortField) => {
    const isActive = sort.field === field;
    const upClassName = isActive && sort.direction === 'asc' ? 'text-zinc-900' : 'text-zinc-300';
    const downClassName = isActive && sort.direction === 'desc' ? 'text-zinc-900' : 'text-zinc-300';

    return (
      <span className="flex flex-col leading-none">
        <ArrowUp size={11} className={upClassName} aria-hidden="true" />
        <ArrowDown size={11} className={downClassName} aria-hidden="true" />
      </span>
    );
  };

  return (
    <PageShell>
      <PageHeader title={t('pages.gapAnalysisTitle')} />

      <SectionCard>
        <div className="border-b border-zinc-200 px-5 py-5 sm:px-6">
          <h2 className="text-[28px] font-semibold leading-tight tracking-tight text-zinc-950 text-balance">
            {headline}
          </h2>
          <p className="mt-2 text-sm text-zinc-500">{data.summary.description}</p>
        </div>

        <div className="grid divide-y divide-zinc-200 sm:grid-cols-2 sm:divide-y-0 xl:grid-cols-6 xl:divide-x">
          <MetricStat label="可见度" value={`${data.summary.visibility.toFixed(1)}%`} delta="+0.0%" />
          <MetricStat label="情感得分" value={data.summary.sentiment} delta="+0" />
          <MetricStat label="平均排名" value={`#${data.summary.position.toFixed(1)}`} delta="+0" />
          <MetricStat label="曝光份额" value={`${data.summary.sov.toFixed(1)}%`} delta="+0.0%" />
          <ModelStat label="表现最佳模型" model={strongestModel} />
          <ModelStat label="表现最弱模型" model={weakestModel} />
        </div>
      </SectionCard>

      <div className="mb-5 mt-8">
        <h2 className="text-base font-semibold text-zinc-950">竞品对比矩阵</h2>
        <p className="text-sm text-zinc-500">各品牌在不同 AI 模型中的可见度评分对比。</p>
      </div>

      <SectionCard>
        <TableFrame>
          <thead>
            <tr>
              <Th align="left">
                <button
                  type="button"
                  onClick={() => toggleSort('brand')}
                  className="flex items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  aria-label={`${sort.field === 'brand' && sort.direction === 'asc' ? '降序' : '升序'}排列品牌`}
                >
                  <span>品牌</span>
                  {renderSortIndicator('brand')}
                </button>
              </Th>
              {data.models.map((model: GapModel) => (
                <Th key={model.id}>
                  <button
                    type="button"
                    onClick={() => toggleSort(model.id)}
                    className="flex min-w-[88px] flex-col items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                    aria-label={`Sort by ${model.name} ${sort.field === model.id && sort.direction === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    <img src={getFaviconUrl(model.website)} alt="" className="h-7 w-7 object-contain" />
                    <span className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                      <span>{model.name}</span>
                      {renderSortIndicator(model.id)}
                    </span>
                  </button>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedBrands.map((brand: GapBrand) => (
              <tr key={brand.id} className={brand.isOwn ? 'bg-zinc-50/60' : 'hover:bg-zinc-50'}>
                <Td align="left" className="min-w-[220px] py-3">
                  <div className="flex items-center gap-3">
                    <img
                      className="h-9 w-9 rounded-xl bg-white p-1.5 object-contain"
                      src={`https://www.google.com/s2/favicons?domain=${brand.domain}&sz=64`}
                      alt=""
                      loading="lazy"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-zinc-950">{brand.name}</div>
                      <div className="truncate text-xs text-zinc-400">{brand.domain}</div>
                    </div>
                  </div>
                </Td>
                {data.models.map((model: GapModel) => {
                  const score = brand.scores[model.id as keyof typeof brand.scores];
                  return (
                    <Td key={model.id} className="py-2">
                      <div
                        className="mx-auto flex h-11 min-w-[72px] items-center justify-center rounded-xl text-sm font-semibold tabular-nums shadow-sm"
                        style={getHeatmapStyle(score)}
                      >
                        {score}
                      </div>
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </SectionCard>
    </PageShell>
  );
};

export default GapAnalysisPage;
